import { Users } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import { platformBadge } from './PancakeDashboardFilters';

function formatDate(ts: string): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return ts;
  }
}

export default function PancakeCustomersTable({ isDark }: { isDark: boolean }) {
  const rawCustomers = usePancakeStore(s => s.customers);
  const customers = {
    total: rawCustomers?.total ?? 0,
    customers: rawCustomers?.customers ?? [],
  };

  const headerClass = `text-left px-4 py-3 font-medium text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`;
  const cellClass = `px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`;
  const cellNameClass = `px-4 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`;

  return (
    <div className="space-y-4">
      {/* Total count */}
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
          <Users className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
        </div>
        <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          Tổng: <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{customers.total.toLocaleString('en-US')}</span> khách hàng
        </span>
      </div>

      {/* Table */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                <th className={headerClass}>Tên</th>
                <th className={headerClass}>SĐT</th>
                <th className={headerClass}>Email</th>
                <th className={headerClass}>Tags</th>
                <th className={headerClass}>Trang</th>
                <th className={headerClass}>Nền tảng</th>
                <th className={headerClass}>Tương tác cuối</th>
                <th className={headerClass}>Ngày tạo</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
              {customers.customers.length === 0 && (
                <tr>
                  <td colSpan={8} className={`px-4 py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Không có khách hàng
                  </td>
                </tr>
              )}
              {customers.customers.map((c) => (
                <tr key={c.id} className={`${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className={cellNameClass}>{c.name || '—'}</td>
                  <td className={cellClass}>
                    {(c.phones || []).length > 0 ? c.phones.join(', ') : '—'}
                  </td>
                  <td className={cellClass}>
                    {(c.emails || []).length > 0 ? c.emails.join(', ') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).length === 0 && <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-300'}`}>—</span>}
                      {(c.tags || []).map((tag) => (
                        <span
                          key={tag.id}
                          className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${isDark ? 'bg-slate-700 text-slate-200' : 'bg-gray-100 text-gray-700'}`}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={cellClass}>{c._pageName}</td>
                  <td className="px-4 py-3">{platformBadge(c._platform)}</td>
                  <td className={cellClass}>{formatDate(c.last_interact_at)}</td>
                  <td className={cellClass}>{formatDate(c.inserted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
